import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Modal from 'react-modal';
import emailjs from '@emailjs/browser';
import { FiX, FiUser, FiMail, FiFileText, FiBriefcase, FiPhone } from 'react-icons/fi';

const ApplicationForm = ({ isOpen, closeModal, jobTitle }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    coverLetter: '',
    position: jobTitle
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    Modal.setAppElement('#root');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare email data
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        coverLetter: formData.coverLetter
      };

      // Send email
      await emailjs.send(
        'service_ndo77n4', //service ID
        'template_sk0ze8r', 
        templateParams,
        'YIq7nbvs7mgGyuR72' //Public ID
      );

      setSubmitStatus('success');
      setTimeout(() => {
        closeModal();
        setFormData({
          name: '',
          email: '',
          phone: '',
          coverLetter: '',
          position: jobTitle
        });
      }, 2000);
    } catch (error) {
      console.error('Submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
<Modal
  isOpen={isOpen}
  onRequestClose={closeModal}
  style={{
    overlay: {
      backgroundColor: 'rgba(0,0,0,0.5)',
      overflow: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      zIndex: 1000
    },
    content: {
      position: 'relative',
      inset: 'auto',
      maxHeight: '85vh',
      overflowY: 'auto',
      width: '100%',
      padding: '0',
      border: 'none',
      borderRadius: '1rem',
      background: 'none'
    }
  }}
>
  <motion.div
    initial={{ scale: 0.95, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    className="bg-white rounded-2xl p-6 sm:p-8 relative min-w-[85vw] sm:min-w-0 sm:max-w-2xl border border-orange-100 shadow-xl"
    style={{ margin: 'auto' }}
  >

    {/* Close Button */}
    <button
      onClick={closeModal}
      className="sticky top-2 right-2 ml-auto block text-[#5F6C8C] hover:text-[#FF7A30] transition-colors z-10 bg-[#FFF3E8] rounded-full p-1"
    >
      <FiX className="w-6 h-6" />
    </button>

    <div className="space-y-6 pb-[60px]">

      {/* Title */}
      <h2 className="text-2xl sm:text-3xl font-bold text-[#2F3E5C] mb-6">
        Apply for {jobTitle}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">

        <div className="grid md:grid-cols-2 gap-6">

          {/* Name */}
          <div>
            <label className="text-[#2F3E5C] mb-2 flex items-center gap-2 font-medium">
              <FiUser className="text-[#FF7A30]" />
              Full Name
            </label>

            <input
              type="text"
              name="name"
              required
              className="w-full bg-[#FFF3E8] rounded-xl p-3 text-[#2F3E5C] placeholder-[#5F6C8C] focus:ring-2 focus:ring-[#FF7A30] focus:outline-none"
              value={formData.name}
              onChange={(e)=>setFormData({...formData,name:e.target.value})}
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-[#2F3E5C] mb-2 flex items-center gap-2 font-medium">
              <FiMail className="text-[#FF7A30]" />
              Email Address
            </label>

            <input
              type="email"
              name="email"
              required
              className="w-full bg-[#FFF3E8] rounded-xl p-3 text-[#2F3E5C] placeholder-[#5F6C8C] focus:ring-2 focus:ring-[#FF7A30] focus:outline-none"
              value={formData.email}
              onChange={(e)=>setFormData({...formData,email:e.target.value})}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="text-[#2F3E5C] mb-2 flex items-center gap-2 font-medium">
              <FiPhone className="text-[#FF7A30]" />
              Phone Number
            </label>

            <input
              type="tel"
              name="phone"
              required
              className="w-full bg-[#FFF3E8] rounded-xl p-3 text-[#2F3E5C] placeholder-[#5F6C8C] focus:ring-2 focus:ring-[#FF7A30] focus:outline-none"
              value={formData.phone}
              onChange={(e)=>setFormData({...formData,phone:e.target.value})}
            />
          </div>

        </div>

        {/* Cover Letter */}
        <div>
          <label className="text-[#2F3E5C] mb-2 flex items-center gap-2 font-medium">
            <FiFileText className="text-[#FF7A30]" />
            Cover Letter
          </label>

          <textarea
            name="coverLetter"
            rows="4"
            className="w-full bg-[#FFF3E8] rounded-xl p-3 text-[#2F3E5C] placeholder-[#5F6C8C] focus:ring-2 focus:ring-[#FF7A30] focus:outline-none"
            value={formData.coverLetter}
            onChange={(e)=>setFormData({...formData,coverLetter:e.target.value})}
          />
        </div>

        {/* Status Messages */}

        {submitStatus === 'success' && (
          <motion.div
            initial={{opacity:0,y:10}}
            animate={{opacity:1,y:0}}
            className="p-3 bg-green-100 text-green-600 rounded-xl"
          >
            Application submitted successfully!
          </motion.div>
        )}

        {submitStatus === 'error' && (
          <motion.div
            initial={{opacity:0,y:10}}
            animate={{opacity:1,y:0}}
            className="p-3 bg-red-100 text-red-600 rounded-xl"
          >
            Error submitting application. Please try again.
          </motion.div>
        )}

        {/* Submit Button */}

        <div className="sticky bottom-0 pt-4 pb-2">

          <motion.button
            whileHover={{scale:1.04}}
            whileTap={{scale:0.96}}
            type="submit"
            disabled={isSubmitting}
            className="
            w-full
            bg-[#FF7A30]
            hover:bg-[#ff6a1c]
            text-white
            px-6
            py-3
            rounded-xl
            font-semibold
            transition
            flex
            items-center
            justify-center
            gap-2
            shadow-md
            "
          >

            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Submitting...
              </>
            ) : (
              "Submit Application"
            )}

          </motion.button>

        </div>

      </form>

    </div>

  </motion.div>
</Modal>
  );
};

export default ApplicationForm;